/* eslint-disable react/no-unescaped-entities */
/**
 * /hustleup/me — verified member profile + my groups + my RSVPs.
 * Reads session token from localStorage.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users, CheckCircle2, MapPin, Mail, Phone, Loader2, LogOut,
  Calendar, ArrowRight, Hourglass, Star,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });
const HU_TOKEN_KEY = "hu_session_token";

export default function HustleUpMePage() {
  const navigate = useNavigate();
  const [member, setMember]   = useState(null);
  const [cats, setCats]       = useState([]);
  const [groups, setGroups]   = useState([]);
  const [rsvps, setRsvps]     = useState([]);
  const [tab, setTab]         = useState("profile");
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    const token = localStorage.getItem(HU_TOKEN_KEY);
    if (!token) { navigate("/hustleup/join"); return; }
    Promise.all([
      api.get(`/api/hustleup/members/me?token=${token}`),
      api.get(`/api/hustleup/categories`),
      api.get(`/api/hustleup/members/me/groups?token=${token}`),
      api.get(`/api/hustleup/members/me/rsvps?token=${token}&when=upcoming`),
    ]).then(([m, c, g, r]) => {
      setMember(m.data); setCats(c.data || []);
      setGroups(g.data?.results || []);
      setRsvps(r.data?.results || []);
      setLoading(false);
    }).catch(e => {
      setError(e?.response?.data?.detail || "Couldn't load profile");
      setLoading(false);
      if (e?.response?.status === 401) {
        localStorage.removeItem(HU_TOKEN_KEY);
        setTimeout(() => navigate("/hustleup/join"), 1500);
      }
    });
  }, [navigate]);

  const signOut = () => {
    localStorage.removeItem(HU_TOKEN_KEY);
    navigate("/hustleup");
  };

  if (loading) {
    return (
      <div style={s.root}>
        <div style={s.loading}><Loader2 className="spin" /> Loading…</div>
        <style>{spinCss}</style>
      </div>
    );
  }
  if (error || !member) {
    return (
      <div style={s.root}>
        <div style={s.card}><h2>Couldn't load profile</h2><p>{error}</p></div>
      </div>
    );
  }

  const interestNames = (member.interests || []).map(slug => {
    const c = cats.find(x => x.slug === slug);
    return c ? { slug, name: c.name, color: c.color } : { slug, name: slug };
  });

  return (
    <div style={s.root} data-testid="hu-me-root">
      <header style={s.header}>
        <a href="/hustleup" style={s.brand}>
          <Users size={20} />
          <span>Hustle<span style={{ color: "#fbbf24" }}>Up</span></span>
        </a>
        <div style={{ display: "flex", gap: 10 }}>
          <a href="/hustlechat" style={s.signOut} data-testid="hu-chat-link">
            <Mail size={14} /> Chat
          </a>
          <button onClick={signOut} style={s.signOut} data-testid="hu-signout">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main style={s.main}>
        <div style={s.card}>
          <div style={s.avatar}>
            {member.first_name?.[0]?.toUpperCase()}
          </div>
          <h1 style={s.h1} data-testid="hu-me-name">
            {member.first_name} {member.last_name || ""}
          </h1>
          <div style={s.metaRow}>
            <MapPin size={14} /> {member.postcode} {member.state}, Australia
          </div>

          {/* ── Tabs ───────────────────────────────────────────── */}
          <div style={s.tabs} role="tablist">
            <button onClick={() => setTab("profile")}
                    style={{ ...s.tab, ...(tab === "profile" ? s.tabActive : {}) }}
                    data-testid="hu-tab-profile">Profile</button>
            <button onClick={() => setTab("groups")}
                    style={{ ...s.tab, ...(tab === "groups" ? s.tabActive : {}) }}
                    data-testid="hu-tab-groups">
              My groups{groups.length ? ` (${groups.length})` : ""}
            </button>
            <button onClick={() => setTab("rsvps")}
                    style={{ ...s.tab, ...(tab === "rsvps" ? s.tabActive : {}) }}
                    data-testid="hu-tab-rsvps">
              My RSVPs{rsvps.length ? ` (${rsvps.length})` : ""}
            </button>
          </div>

          {tab === "profile" && (
            <div data-testid="hu-tab-panel-profile">
              <div style={s.verifyGrid}>
                <div style={s.verifyItem}>
                  <Mail size={16} color="#16a34a" />
                  <div>
                    <div style={s.verifyLabel}>Email</div>
                    <div style={s.verifyValue}>{member.email}</div>
                  </div>
                  <span style={s.checkmark}><CheckCircle2 size={16} /></span>
                </div>
                <div style={s.verifyItem}>
                  <Phone size={16} color="#16a34a" />
                  <div>
                    <div style={s.verifyLabel}>Mobile</div>
                    <div style={s.verifyValue}>{member.mobile}</div>
                  </div>
                  <span style={s.checkmark}><CheckCircle2 size={16} /></span>
                </div>
              </div>

              <h3 style={s.h3}>Your interests</h3>
              <div style={s.tags} data-testid="hu-me-interests">
                {interestNames.map(i => (
                  <span key={i.slug} style={{ ...s.tag,
                                              borderLeftColor: i.color || "#fbbf24" }}>
                    {i.name}
                  </span>
                ))}
              </div>

              <div style={s.footerNote}>
                <CheckCircle2 size={14} color="#16a34a" /> Free member · joined{" "}
                {new Date(member.joined_at).toLocaleDateString("en-AU")}
                {member.linked_user_id && (
                  <> · also linked to your Hustle account</>
                )}
              </div>
            </div>
          )}

          {tab === "groups" && (
            <div data-testid="hu-tab-panel-groups">
              {groups.length === 0 ? (
                <div style={s.empty}>
                  <Users size={28} color="#cbd5e1" />
                  <p>You haven't joined any groups yet.</p>
                  <button onClick={() => navigate("/hustleup/groups")}
                          style={s.cta} data-testid="hu-go-browse-groups">
                    Browse groups <ArrowRight size={14} />
                  </button>
                </div>
              ) : (
                <>
                  {groups.map(g => (
                    <button key={g.slug}
                            onClick={() => navigate(`/hustleup/groups/${g.slug}`)}
                            style={s.row}
                            data-testid={`hu-mygrp-${g.slug}`}>
                      <div>
                        <div style={s.rowTitle}>{g.name}</div>
                        <div style={s.rowMeta}>
                          <MapPin size={11} /> {g.region}
                          {g.is_host && <span style={s.hostBadge}>
                            <Star size={9} /> Host
                          </span>}
                          {" · "}{g.member_count} member{g.member_count === 1 ? "" : "s"}
                        </div>
                      </div>
                      <ArrowRight size={16} color="#94a3b8" />
                    </button>
                  ))}
                  <button onClick={() => navigate("/hustleup/groups/new")}
                          style={{ ...s.cta, width: "100%", marginTop: 14 }}
                          data-testid="hu-go-create-group">
                    Start your own group <ArrowRight size={14} />
                  </button>
                </>
              )}
            </div>
          )}

          {tab === "rsvps" && (
            <div data-testid="hu-tab-panel-rsvps">
              {rsvps.length === 0 ? (
                <div style={s.empty}>
                  <Calendar size={28} color="#cbd5e1" />
                  <p>No upcoming RSVPs yet — once you RSVP to an event,
                     it'll appear here with a tap-to-add-to-calendar.</p>
                  <button onClick={() => navigate("/hustleup/groups")}
                          style={s.cta} data-testid="hu-go-browse-events">
                    Find events <ArrowRight size={14} />
                  </button>
                </div>
              ) : (
                rsvps.map(e => (
                  <button key={e.event_id || e._id}
                          onClick={() => navigate(`/hustleup/event/${e.event_id || e._id}`)}
                          style={s.row}
                          data-testid={`hu-myrsvp-${e.event_id || e._id}`}>
                    <div>
                      <div style={s.rowTitle}>{e.title}</div>
                      <div style={s.rowMeta}>
                        <Calendar size={11} />{" "}
                        {new Date(e.starts_at).toLocaleString("en-AU", {
                          weekday: "short", day: "numeric", month: "short",
                          hour: "numeric", minute: "2-digit",
                        })}
                        {" · "}
                        {e.my_rsvp === "waitlisted"
                          ? <><Hourglass size={10} /> Waitlisted</>
                          : <><CheckCircle2 size={10} color="#16a34a" /> Going</>}
                      </div>
                    </div>
                    <ArrowRight size={16} color="#94a3b8" />
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </main>

      <style>{spinCss}</style>
    </div>
  );
}

const spinCss = `.spin{animation:spin 1s linear infinite}
                  @keyframes spin{to{transform:rotate(360deg)}}`;

const s = {
  root: { background: "#fffef5", minHeight: "100vh",
          fontFamily: "Inter, system-ui, sans-serif", color: "#0f172a" },
  header: { display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "18px 32px",
            background: "#0f172a", color: "white" },
  brand: { display: "inline-flex", alignItems: "center", gap: 8,
           color: "#fbbf24", textDecoration: "none", fontWeight: 800,
           fontFamily: "Georgia, serif", fontSize: 20 },
  signOut: { background: "transparent", color: "#94a3b8", border: "1px solid #1e293b",
             padding: "8px 14px", borderRadius: 999, cursor: "pointer",
             display: "inline-flex", alignItems: "center", gap: 6,
             fontSize: 12, fontWeight: 600, textDecoration: "none" },
  main: { maxWidth: 640, margin: "0 auto", padding: "40px 20px 80px" },
  card: { background: "white", padding: 36, borderRadius: 18,
          boxShadow: "0 20px 60px rgba(0,0,0,0.06)" },
  loading: { display: "flex", alignItems: "center", justifyContent: "center",
             padding: 80, gap: 12, color: "#475569" },
  avatar: { width: 80, height: 80, borderRadius: "50%",
            background: "#fbbf24", color: "#0f172a",
            display: "inline-flex", alignItems: "center",
            justifyContent: "center", fontSize: 32, fontWeight: 800,
            fontFamily: "Georgia, serif", marginBottom: 14 },
  h1: { fontFamily: "Georgia, serif", fontSize: 30, fontWeight: 800,
        letterSpacing: "-0.025em", margin: "0 0 8px" },
  h3: { fontSize: 12, fontWeight: 800, letterSpacing: "0.08em",
        textTransform: "uppercase", color: "#64748b",
        margin: "20px 0 12px" },
  metaRow: { display: "flex", alignItems: "center", gap: 6,
             color: "#475569", fontSize: 14, marginBottom: 24 },
  tabs: { display: "flex", gap: 6, background: "#fef3c7",
          padding: 4, borderRadius: 999, marginBottom: 20 },
  tab: { flex: 1, background: "transparent", border: "none",
         padding: "9px 4px", borderRadius: 999, fontWeight: 700,
         cursor: "pointer", color: "#92400e", fontSize: 12,
         letterSpacing: 0.2 },
  tabActive: { background: "#0f172a", color: "#fbbf24" },
  verifyGrid: { display: "grid", gridTemplateColumns: "1fr 1fr",
                gap: 12, marginBottom: 8 },
  verifyItem: { display: "flex", alignItems: "center", gap: 10,
                padding: 14, background: "#f0fdf4",
                border: "1px solid #bbf7d0", borderRadius: 12 },
  verifyLabel: { fontSize: 11, fontWeight: 700, color: "#16a34a",
                 textTransform: "uppercase", letterSpacing: "0.06em" },
  verifyValue: { fontSize: 13, color: "#0f172a", fontWeight: 500,
                 wordBreak: "break-all" },
  checkmark: { marginLeft: "auto", color: "#16a34a" },
  tags: { display: "flex", flexWrap: "wrap", gap: 8 },
  tag: { background: "#fef3c7", color: "#92400e", padding: "6px 14px",
         borderRadius: 8, fontSize: 13, fontWeight: 600,
         borderLeft: "3px solid #fbbf24" },
  footerNote: { marginTop: 28, paddingTop: 18,
                borderTop: "1px solid #f1f5f9",
                color: "#64748b", fontSize: 12,
                display: "flex", alignItems: "center", gap: 6 },
  empty: { textAlign: "center", padding: "32px 12px", color: "#64748b",
           display: "flex", flexDirection: "column", alignItems: "center",
           gap: 10 },
  row: { display: "flex", width: "100%", justifyContent: "space-between",
         alignItems: "center", padding: 14, marginBottom: 10,
         background: "white", border: "1px solid #e8e2d4",
         borderRadius: 12, cursor: "pointer", textAlign: "left",
         font: "inherit", color: "inherit" },
  rowTitle: { fontSize: 15, fontWeight: 700, marginBottom: 3 },
  rowMeta: { display: "inline-flex", alignItems: "center", gap: 5,
             fontSize: 12, color: "#64748b", flexWrap: "wrap" },
  hostBadge: { display: "inline-flex", alignItems: "center", gap: 3,
               background: "#fef3c7", color: "#92400e",
               padding: "2px 8px", borderRadius: 999, fontSize: 10,
               fontWeight: 700, marginLeft: 6 },
  cta: { display: "inline-flex", alignItems: "center", justifyContent: "center",
         gap: 6, background: "#0f172a", color: "#fbbf24", border: "none",
         padding: "12px 22px", borderRadius: 999, fontWeight: 700,
         cursor: "pointer", fontSize: 13 },
};
